// =============================================================================
// Module Core - Access Control Definitions (Lapyno)
// Configuration centrale pour le SaaS de gestion de projet
// =============================================================================

import {
  FolderKanban,
  Settings,
  type LucideIcon,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

// Lapyno is a single-module SaaS - only projects + admin for settings
export type ModuleCode = 'projects' | 'admin';

// Role hierarchy: owner > admin > member > guest
export type AppRole = 'owner' | 'admin' | 'member' | 'guest';

export interface ModuleDefinition {
  code: ModuleCode;
  label: string;
  icon: LucideIcon;
  routeBase: string;
  description: string;
  color: string;
}

export interface ModuleVisibilityRule {
  /** Rôles qui ont accès par défaut */
  allowedRoles: AppRole[];
  /** Si true, nécessite aussi un module_access enabled */
  requiresModuleAccess: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULES DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

export const MODULES: Record<ModuleCode, ModuleDefinition> = {
  projects: {
    code: 'projects',
    label: 'Projets',
    icon: FolderKanban,
    routeBase: '/projects',
    description: 'Gestion des projets et tâches',
    color: 'hsl(var(--primary))',
  },
  admin: {
    code: 'admin',
    label: 'Paramètres',
    icon: Settings,
    routeBase: '/admin/access',
    description: 'Membres, rôles et accès',
    color: 'hsl(var(--muted-foreground))',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// VISIBILITY RULES
// ─────────────────────────────────────────────────────────────────────────────

export const MODULE_VISIBILITY_RULES: Record<ModuleCode, ModuleVisibilityRule> = {
  projects: {
    allowedRoles: ['owner', 'admin', 'member', 'guest'],
    requiresModuleAccess: false,
  },
  admin: {
    allowedRoles: ['owner', 'admin'],
    requiresModuleAccess: false,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ROLE DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

// Liste des rôles système (simplified v2)
export const SYSTEM_ROLES: AppRole[] = ['owner', 'admin', 'member', 'guest'];

// UX Wording ClickUp-like : member = Membre, guest = Invité
export const ROLE_LABELS: Record<AppRole, string> = {
  owner: 'Propriétaire',
  admin: 'Administrateur',
  member: 'Membre',
  guest: 'Invité',
};

export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  owner: 'Accès total au workspace et tous les contenus',
  admin: 'Gère les membres, rôles et paramètres. Accès implicite à tous les contenus',
  member: 'Voit tous les espaces et projets publics. Les contenus privés nécessitent un partage explicite',
  guest: 'Accès limité aux ressources partagées explicitement (invité)',
};

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSIONS
// ─────────────────────────────────────────────────────────────────────────────

export const PERMISSION_CODES = {
  ORG_MANAGE: 'org.manage',
  MEMBERS_MANAGE: 'members.manage',
  ROLES_MANAGE: 'roles.manage',
  PROJECTS_MANAGE: 'projects.manage',
  CATALOG_MANAGE: 'catalog.manage',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calcule si un rôle + accès modules permet de voir un module donné
 */
export function canRoleSeeModule(
  role: AppRole | null,
  moduleCode: ModuleCode,
  moduleAccess: ModuleCode[]
): boolean {
  if (!role) return false;

  const rules = MODULE_VISIBILITY_RULES[moduleCode];
  
  // Check if role is allowed
  if (!rules.allowedRoles.includes(role)) {
    // Role not allowed by default, check if has explicit module access
    if (rules.requiresModuleAccess && moduleAccess.includes(moduleCode)) {
      return true;
    }
    return false;
  }

  // Role is allowed
  // If module requires access, check for owner/admin bypass or explicit access
  if (rules.requiresModuleAccess) {
    if (role === 'owner' || role === 'admin') {
      return true;
    }
    return moduleAccess.includes(moduleCode);
  }

  return true;
}

/**
 * Retourne tous les modules visibles pour un rôle + accès
 */
export function getVisibleModules(
  role: AppRole | null,
  moduleAccess: ModuleCode[]
): ModuleDefinition[] {
  if (!role) return [];

  return Object.values(MODULES).filter((mod) =>
    canRoleSeeModule(role, mod.code, moduleAccess)
  );
}

/**
 * Vérifie si un rôle org peut gérer un aspect donné (org-level check only)
 * For space/project management, use canManageSpace/canManageProject which
 * also check for 'full' permission at the resource level.
 */
export function canManage(role: AppRole | null, aspect: 'members' | 'roles' | 'modules'): boolean {
  if (!role) return false;
  return role === 'owner' || role === 'admin';
}

/**
 * Vérifie si l'utilisateur peut gérer un Space
 * True if: (owner/admin org) OR (has 'full' permission on the space)
 */
export function canManageSpaceWithPermission(
  role: AppRole | null,
  spacePermission: 'full' | 'edit' | 'comment' | 'view' | null
): boolean {
  // Owner/Admin org bypass
  if (role === 'owner' || role === 'admin') return true;
  // Otherwise, need 'full' permission on the space
  return spacePermission === 'full';
}
